import React, { useEffect, useState } from "react";
import {
  Input,
  Label,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  addGenre,
  editGenre,
  getGenre,
  getGenreCount,
} from "../services/genre-services";
import { resetError, resetState } from "../controller/genre-slice";
import { PHONE_NUMBER_MAXLENGTH } from "@constants/constants";
import ImageFileUpload from "../../../components/custom-image/ImageFileUpload";
import { Client } from "@helpers/helpers";
import { API_METHOD } from "@constants/constants";
import { url } from "@constants/url-constants";
import ViewModal from "@components/custom-table/ViewModel";
import {
  genreSchema,
  genreUpdateSchema,
} from "../../../helpers/validation-schema";
import CustomLabel from "../../../components/CustomLabel";
import { NAME_REGEX } from "../../../constants/constants";
import { handleInput } from "../../../helpers/helpers";

export const AddGenre = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const genreId = pathname.split("/")[3];
  const success = useSelector((state) => state.genre.success);
  const genre = useSelector((state) => state.genre.genres);
  const loading = useSelector((state) => state.genre.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.genre.successMessage);
  const [viewModel, setViewModel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const errorMessage = useSelector((state) => state.genre.errorMessage);
  const [fileInfo, extractFileMetadata] = ImageFileUpload();

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(route === "add" ? genreSchema : genreUpdateSchema),
  });

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getGenreCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, [route, setValue]);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getGenre(genreId));
    }
  }, [genreId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/genre?current_page=${pageQuery}`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);
      setIsLoading(false);
      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (genre) {
      setValue("name", genre?.name);
      setValue("sorting", genre?.sorting);
    }
  }, [genre, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    let tempImageUrl = "";
    if (data.imageUpload) {
      const generatePresignedUrl = await handleImageUpload(data.imageUpload);
      if (generatePresignedUrl.imageTemp) {
        tempImageUrl = generatePresignedUrl.imageTemp;
      } else {
        //show error message
        setIsLoading(false);
        // hide loader or enable button
      }
    }

    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        thumbnail: {
          image_url: tempImageUrl,
          ...fileInfo,
        },
      };
      saveGenreForm(formattedData);
    } else {
      const formattedData = {
        ...data,
        sorting: genre?.sorting,
        thumbnail: tempImageUrl
          ? {
              image_url: tempImageUrl,
              ...fileInfo,
            }
          : genre?.thumbnail,
      };
      saveGenreForm(formattedData, genreId);
    }
  };

  const saveGenreForm = (formattedData, genreId = "") => {
    if (genreId) {
      dispatch(editGenre({ id: genreId, data: formattedData }));
    } else {
      dispatch(addGenre(formattedData));
    }
  };
  const handleImageUpload = async (file) => {
    const fileExtension = {
      extensions: [file?.name.split(".").pop()?.toLowerCase()],
    };

    try {
      const response = await Client(
        API_METHOD.POST,
        url.presigned_url,
        fileExtension
      );
      const presignedUrl = response.data.data.items[0].preSignedUrl;

      const imageTemp = response.data.data.items[0].fileName;

      const saveImage = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        redirect: "follow",
      });
      if (saveImage.status === 200) {
        return { imageTemp };
      } else {
        return { type: "error", message: "Failed to Upload Image" };
      }
    } catch (error) {
      return { type: "error", message: "Failed to Upload Image" };
    }
  };

  // const onSubmit = (data) => {

  //   if (route !== "edit") {
  //     const formattedData = {
  //       ...data,
  //       sorting: parseInt(data.sorting),
  //     };
  //     dispatch(addGenre(formattedData));
  //   } else {
  //     const formattedData = {
  //       ...data,
  //       sorting: parseInt(data.sorting),
  //     };

  //     dispatch(editGenre({ id: genreId, data: formattedData }));
  //   }
  // };

  const handleViewImage = () => {
    setViewModel(true);
  };
  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };

  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          {viewModel && (
            <ViewModal
              show={viewModel}
              onViewClick={handleViewImage}
              onCloseClick={() => setViewModel(false)}
              data={genre?.thumbnail?.image_url}
            />
          )}
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit Genre"
                  : route === "view"
                  ? "View Genre"
                  : "Create Genre"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                    <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onChange={(e) => handleInputChange(e, "sorting")}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Genre Name" isRequired>
                        Genre Name
                      </CustomLabel>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Genre Name"
                              invalid={!!errors.name}
                              onInput={(e) => handleInput(e, NAME_REGEX)}
                              maxLength={NAME_MAXLENGTH}
                            />
                            {errors.name && (
                              <FormFeedback>{errors.name.message}</FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Image" isRequired>
                        Image
                      </CustomLabel>

                      {route !== "add" ? (
                          <button
                            type="button"
                            className="view-button"
                            onClick={() => {
                              handleViewImage();
                            }}
                          >
                            View
                          </button>
                        ) : null}
                        
                      <Controller
                        name="imageUpload"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <div>
                            <Input
                              type="file"
                              accept="image/jpeg,image/png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                field.onChange(file);
                                extractFileMetadata(file);
                              }}
                              invalid={!!errors.imageUpload}
                            />
                            {errors.imageUpload && (
                              <FormFeedback>
                                {errors.imageUpload.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/genre?current_page=${pageQuery}`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button
                          type="submit"
                          color={isLoading ? "success" : "primary"}
                        >
                          {isLoading ? (
                            <>Loading...</>
                          ) : (
                            <>
                              {route === "edit" ? "Edit Genre" : "Create Genre"}
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddGenre;
